/**
 * capasArquitectura -- la grilla capa x repo de la Sala de Maquinas (Josse "va con las capas", Ares #38145, paso 4).
 *
 * Lee GET /api/v1/salud/arquitectura-capas (contrato A #38194, nexo-api PR 668): la ultima foto de cada repo con las
 * siete claves de #38145, cada una {valor, lista, como, motivo}. Aqui viven las reglas; NxCapasArquitectura.js pinta.
 *
 * SIN META NO HAY COLOR: ninguna meta esta aprobada todavia (las fija Josse), asi que todo numero se ve en gris con su
 * lista. Un valor null no es cero: se dice "sin medir" y por que (el motivo que trae el repo).
 *
 * @module application/salud/capasArquitectura
 */
import { diasDeLaArquitectura, estadoDeFrescura } from './frescura.js';

/** Las siete claves del contrato, en el orden de la grilla, con lo que mide cada una en palabras del dueno. */
export const CAPAS = [
  { clave: 'capa0_conocidos_pct', capa: 0, nombre: 'Lo conocemos', unidad: '%', frase: 'De los archivos de código, cuántos nombra un nodo del árbol. La lista: los que no conoce nadie.' },
  { clave: 'capa1_huerfanos', capa: 1, nombre: 'Archivos sin uso', unidad: '', frase: 'Archivos que nadie importa ni llama.' },
  { clave: 'capa1_dormidos', capa: 1, nombre: 'Código dormido', unidad: '', frase: 'Funciones, clases o servicios que nadie usa.' },
  { clave: 'capa1_binarios', capa: 1, nombre: 'Binarios en el repo', unidad: '', frase: 'exe, dll, pfx, pem, zip o publicaciones que no deberían estar versionados.' },
  { clave: 'capa1_duplicados', capa: 1, nombre: 'Archivos repetidos', unidad: '', frase: 'El mismo contenido en dos o más rutas.' },
  { clave: 'capa1_ramas_30d', capa: 1, nombre: 'Ramas viejas', unidad: '', frase: 'Ramas sin commits hace más de 30 días.' },
  { clave: 'capa1_prs_14d', capa: 1, nombre: 'PRs viejos', unidad: '', frase: 'PRs abiertos hace más de 14 días.' },
];

/** Los repos en el orden de la grilla (los nombres de salud_snapshot, A #38167); uno que no publico queda sin medir. */
export const REPOS = [
  { repo: 'nexo-api', nombre: 'API' },
  { repo: 'panel-v2', nombre: 'Panel' },
  { repo: 'nexo-desktop', nombre: 'Escritorio' },
];

/** Como se dicen los repos que publican sin estar en REPOS (solo aparecen si tienen foto). */
const NOMBRES_DE_MAS = { 'nexo-arcade': 'Arcade', 'nexo-web': 'Web', 'nexo-center': 'Nexo Center' };

/** Cuantas filas de la lista se ven antes de "y N mas". */
export const VISIBLES = 8;

/** Una fila de la lista en texto: viene como texto, arreglo u objeto segun el repo que la midio. */
export function textoDelItem(item) {
  if (item === null || item === undefined) return '';
  if (Array.isArray(item)) return item.map(textoDelItem).filter(Boolean).join(' · ');
  if (typeof item === 'object') return Object.values(item).map(textoDelItem).filter(Boolean).join(' · ');
  return String(item);
}

/** Lo que dice la celda: el numero con su unidad, o "sin medir". Nunca un cero por falta de dato. */
export function textoDelValor(valor, unidad = '') {
  if (valor === null || valor === undefined || valor === '' || Number.isNaN(Number(valor))) return 'sin medir';
  const n = Number(valor);
  const t = Number.isInteger(n) ? n.toLocaleString('es-CO') : n.toLocaleString('es-CO', { maximumFractionDigits: 1 });
  return unidad ? `${t} ${unidad}`.replace(' %', '%') : t;
}

const numOrNull = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

function leerCapa(det) {
  const d = det && typeof det === 'object' ? det : {};
  const lista = Array.isArray(d.lista) ? d.lista : [];
  const valor = d.valor === undefined ? null : d.valor;
  return { valor, lista, como: typeof d.como === 'string' ? d.como : '', motivo: d.motivo ?? d.no_medido ?? null,
    conocidos: numOrNull(d.conocidos), total: numOrNull(d.total) };
}

/**
 * La grilla, pura sobre lo leido. fotos = la respuesta del GET. Los repos de REPOS van siempre (sin foto: sin medir);
 * uno que llegue y no este en REPOS (nexo-arcade, si publica) se agrega al final.
 * -> {repos:[{repo,nombre,fecha,sha,medidoAt,fuente,publico}], filas:[{...CAPAS[i], celdas:[{repo,valor,texto,lista,
 *     visibles,resto,como,motivo}]}], publicaron}
 */
export function grillaDeCapas(fotos) {
  const porRepo = new Map();
  for (const f of Array.isArray(fotos) ? fotos : []) {
    const repo = f?.repo ?? f?.Repo;
    if (repo) porRepo.set(String(repo), f);
  }
  const conocidos = new Set(REPOS.map((r) => r.repo));
  const repos = [...REPOS, ...[...porRepo.keys()].filter((r) => !conocidos.has(r)).sort().map((r) => ({ repo: r, nombre: NOMBRES_DE_MAS[r] || r }))]
    .map((r) => {
      const f = porRepo.get(r.repo);
      return {
        ...r, publico: !!f, fecha: f?.fecha ?? null, sha: f ? String(f.sha ?? '').slice(0, 7) : '',
        medidoAt: f?.medido_at ?? f?.medidoAt ?? null, fuente: f?.fuente ?? '',
      };
    });
  const filas = CAPAS.map((c) => ({
    ...c,
    celdas: repos.map((r) => {
      const f = porRepo.get(r.repo);
      const capas = f?.capas ?? f?.Capas ?? {};
      const k = leerCapa(capas[c.clave]);
      const medido = r.publico && k.valor !== null;
      const motivo = !r.publico ? 'este repo todavía no publicó su foto' : (k.valor === null ? (k.motivo || 'el repo no dijo por qué') : null);
      return {
        repo: r.repo, valor: medido ? Number(k.valor) : null, texto: medido ? textoDelValor(k.valor, c.unidad) : 'sin medir',
        lista: k.lista, visibles: k.lista.slice(0, VISIBLES).map(textoDelItem), resto: Math.max(0, k.lista.length - VISIBLES),
        como: k.como, motivo, conocidos: medido ? k.conocidos : null, total: medido ? k.total : null,
      };
    }),
  }));
  return { repos, filas, publicaron: repos.filter((r) => r.publico).length };
}

/** Por que no hay grilla, por causa (y en dev, a donde pregunta). */
export function porQueNoHay(motivo, env = 'prod') {
  if (env === 'dev') return 'Estás en dev: el panel le pregunta a la API local. En producción el GET de capas es el de nexo-api 668.';
  switch (motivo) {
    case 'NO_PUBLICADO': return 'Falta publicar la lectura de capas en la API (nexo-api 668). Cuando salga, esta grilla se enciende sola.';
    case 'SIN_SESION': return 'La sesión no deja leer las capas: vuelve a entrar.';
    case 'PROHIBIDO': return 'Las capas son solo para el equipo Nexo.';
    case 'VACIO': return 'La API respondió sin fotos: el job diario de capas (nexo-center 100) todavía no ha corrido.';
    default: return 'No pudimos hablar con la API ahora. El resto de la Sala sigue igual.';
  }
}

/**
 * El dial "La arquitectura aguanta?" (Ares #38227): resume las capas en un vistazo; el detalle sigue en la grilla.
 *   peor   = el repo con MENOS codigo conocido (capa 0), solo entre los que lo midieron
 *   basura = la suma de la capa 1 de todos los repos que publicaron; lo no medido no se cuenta como 0 (sinContar)
 *   fecha  = la foto MAS VIEJA de las que entran, y su frescura con las reglas de frescura.js
 * hoy = 'YYYY-MM-DD' de Bogota. -> {fotos, peor, basura, sinContar, fecha, dias, frescura}
 */
export function resumenDeCapas(fotos, hoy) {
  const g = grillaDeCapas(fotos);
  const publicados = g.repos.filter((r) => r.publico);
  const c0 = g.filas.find((f) => f.clave === 'capa0_conocidos_pct');
  const medidos0 = c0.celdas.map((c, i) => ({ repo: g.repos[i].repo, nombre: g.repos[i].nombre, valor: c.valor })).filter((x) => x.valor !== null);
  const peor = medidos0.length ? medidos0.reduce((a, b) => (b.valor < a.valor ? b : a)) : null;
  let basura = 0; let medidos1 = 0; let sinContar = 0;
  for (const f of g.filas.filter((x) => x.capa === 1)) {
    f.celdas.forEach((c, i) => {
      if (!g.repos[i].publico) return;
      if (c.valor === null) sinContar++; else { basura += c.valor; medidos1++; }
    });
  }
  const fechas = publicados.map((r) => String(r.fecha || '').slice(0, 10)).filter((f) => /^\d{4}-\d{2}-\d{2}$/.test(f)).sort();
  const fecha = fechas[0] || null;
  const dias = fecha ? diasDeLaArquitectura({ arquitectura: fecha, hoy }) : null;
  return { fotos: publicados.length, peor, casa: conocidoDeLaCasa(c0, g.repos), basura: medidos1 ? basura : null, sinContar, fecha, dias, frescura: estadoDeFrescura(dias) };
}

/**
 * LO CONOCIDO DE TODA LA CASA (Ares #40070; Josse 31-oct mirando el dial: "Solo tenemos 0.6 de arquitectura?"). El dial
 * mostraba como numero grande el repo MENOS conocido y se leia como el total. Ahora el numero grande es la suma de
 * archivos conocidos sobre la suma de archivos de codigo de los repos que midieron, y cada repo va en su barra.
 * Total de un repo: el que publico; si no lo publico (la API y el escritorio no lo mandan), conocidos + largo de la
 * lista, porque la lista de la capa 0 son justo los desconocidos (comprobado 31-oct en web, Arcade y panel: suma
 * exacta). Un repo sin conocidos no entra en la suma: se cuenta en sinContar, nunca como 0.
 * -> {conocidos, total, pct, repos:[{repo, nombre, conocidos, total, pct}], sinContar} | null si ninguno sirve
 */
export function conocidoDeLaCasa(c0, repos) {
  const filas = [];
  let sinContar = 0;
  (c0?.celdas || []).forEach((c, i) => {
    if (c.valor === null) return;
    const total = c.total ?? (c.conocidos !== null ? c.conocidos + (c.lista || []).length : null);
    if (c.conocidos === null || !total) { sinContar++; return; }
    filas.push({ repo: repos[i].repo, nombre: repos[i].nombre, conocidos: c.conocidos, total, pct: Math.round((1000 * c.conocidos) / total) / 10 });
  });
  if (!filas.length) return null;
  const conocidos = filas.reduce((n, f) => n + f.conocidos, 0);
  const total = filas.reduce((n, f) => n + f.total, 0);
  return { conocidos, total, pct: Math.round((1000 * conocidos) / total) / 10, repos: filas, sinContar };
}
